import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConversation,
  fetchConversationAssetAnalytics,
  fetchConversationAssets,
  fetchAssetsForSections,
  fetchRecipientsForConversation,
  removeOpportunityFromConversation,
  invalidateConversation,
  addOpportunityToShare,
} from "src/redux/actions/conversation";
import userRoleService from "src/utils/userRoles.service";
import { IntegraionService } from "src/utils/integration.service";
import { Conversation, Opportunity as OpportunityType } from "src/types/conversations";
import useMeasure from "react-use-measure";
import {
  generateMetricPercentage,
  generateTimeSpent,
} from "src/utils/conversationMetrics.service";
import { RootState } from "src/redux/store";

import { Col, Row } from "react-bootstrap";
import Actions from "src/components/actionbar";
import { BannerView } from "src/components/banner";
import Tooltip from "src/components/tooltip";
import pluralize from "pluralize";
import TimeAgo from "react-timeago";
import { useSpring, animated } from "@react-spring/web";
import MapDeals from "../mapDeals";
import { TabBar } from "src/components/tabbar";
import RecipientsContainer from "../recipients";
import { SortByAction } from "../sortBy";
import { ConversationDetailLoader } from "../loaders";
import TimeLine from "../timeLine";
import { SectionShare } from "src/components/contact/components/sectionShare";
import Delete from "../delete";
import Preview from "../preview";
import Resend from "../resend";
import AssetShare from "src/components/contact/components/assetShare";
import { useNavigate } from "react-router-dom";
import { useConversationAdoption } from "src/redux/actions/adoption/conversation";
import { TRACK_CONVERSATION_DETAIL_SCREEN_VIEW } from "src/constants/adoption/eventTypes";
import EventService from "src/utils/event.service";
import EmptyMessage from "src/components/emptyMessage";
import RemoveDeal from "../removeDeal";

import _ from "lodash";
import styles from "./conversationDetail.module.scss";
import cx from "classnames";
import { DEAL_ROOM_EXPERIENCE } from "src/constants/collections";
import Text from "src/components/text";

interface IConversationDetail {
  id: string | undefined;
  selectedFilter: string;
}

const ConversationDetail: React.FC<IConversationDetail> = ({
  id,
  selectedFilter,
}) => {
  //mixpanel events
  const { trackPageViewed, trackConversationViewed } =
    useConversationAdoption();

  useEffect(() => {
    trackPageViewed(TRACK_CONVERSATION_DETAIL_SCREEN_VIEW);
  }, []);

  //states
  const [showDealValueBreakup, setShowDealValueBreakup] = React.useState(false);
  const [removeDeal, setRemoveDeals] = React.useState<{show:boolean, opportunity: string | null}>({
    show: false,
    opportunity: null,
  });
  const [showMapDeals, setShowMapDeals] = React.useState(false);
  const [showTimeLine, setShowTimeLine] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const [showResend, setShowResend] = React.useState(false);
  const [showDelete, setShowDelete] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);
  const [sortPreferances, setSortPreferances] = React.useState({
    sortBy: "activity_date",
    orderBy: "DESC",
  });

  //selectors
  const {
    conversation,
    recipients,
    loadingAssets,
    loaded,
    loading,
    conversationState,
    assets,
    sections,
    isListEmpty,
  } = useSelector((state: RootState) => {
    const conversationState = _.get(
      state.conversations,
      `conversation.${id}`,
      {},
    );
    return {
      conversation: _.get(conversationState, `item`, {}) as Conversation,
      assets: _.get(conversationState, `assets`, []),
      sections: _.get(conversationState, `sections`, []),
      recipients: _.get(conversationState, `recipients`, {}),
      loadingAssets: _.get(conversationState, `loadingAssets`, false),
      loading: _.get(conversationState, `loading`, false),
      loaded: _.get(conversationState, `loaded`, false),
      conversationState,
      isListEmpty: _.get(state.conversations.conversations, "items", []).length === 0 && !_.get(state.conversations.conversations,"loading", false),
    };
  });

  //constants
  const sortOptions = [
    {
      label: "Most active",
      value: "most_active",
    },
    {
      label: "Last Activity",
      value: "activity_date",
    },
  ];
  const hasSections = _.size(sections) > 0;
  const isLiveCollection = _.get(conversation,'collection.live', false);
  const [containerRef, containerBounds] = useMeasure();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [childrenAnimation] = useSpring(
    {
      opacity: showDealValueBreakup ? 1 : 0,
      height: showDealValueBreakup ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    },
    [showDealValueBreakup, containerBounds],
  );

  //side effects
  useEffect(() => {
    if (!loading && loaded) {
      dispatch(
        fetchRecipientsForConversation({
          id: id!,
          page: 0,
          sortBy: "activity_date",
          orderBy: "DESC",
          filter: selectedFilter,
        }),
      );
      dispatch(
        fetchConversationAssets({
          id: id!,
          type: selectedFilter,
        }),
      );
    }
  }, [loading, loaded]);

  useEffect(() => {
    if (id) {
      setActiveTab(0);
      setSortPreferances({
        sortBy: "activity_date",
        orderBy: "DESC",
      });
      setShowDelete(false);
      setShowDealValueBreakup(false);
      dispatch(fetchConversation(id));
      const source = new URLSearchParams(_.get(window, "location.search")).get(
        "source",
      );
      if (EventService.triggerEventForSource(source)) {
        trackConversationViewed();
      }
    }
  }, [id]);

  const loadMoreRecipientsForConversation = (page: number) => {
    dispatch(
      fetchRecipientsForConversation({
        id: id!,
        page: page,
        sortBy: sortPreferances.sortBy,
        orderBy: sortPreferances.orderBy,
        filter: selectedFilter,
      }),
    );
  }

  //functions
  const isCollection = () => {
      return _.get(conversation, 'collection') && _.get(conversation, "assetViewer") && _.get(conversation, "viewerType");
  }

  const isDealRoom = () => {
      return _.get(conversation, "viewerType", "") === DEAL_ROOM_EXPERIENCE
        ? true
        : false;
    };
  
  const redirectToCollection = (url : string) => {
    dispatch(invalidateConversation());
    navigate(url);
  }

  const handleDealValueBreakup = () => {
    setShowDealValueBreakup(!showDealValueBreakup);
  };

  const handleFilterChange = (chosenFilter: {
    sortBy: string;
    orderBy: string;
  }) => {
    setSortPreferances(chosenFilter);
    dispatch(
      fetchRecipientsForConversation({
        id: id!,
        page: 0,
        sortBy: chosenFilter.sortBy,
        orderBy: chosenFilter.orderBy,
        filter: selectedFilter,
      }),
    );
  };

  //render component
  if ((loading || !id) && !isListEmpty) {
    return <ConversationDetailLoader />;
  }

  if (loading === false && !conversation?.id) {
    return (
      <EmptyMessage
        className={styles.emptyMessage}
        image={"/images/conversationNotFound.svg"}
        message="The conversation you're looking for doesn't exist."
        action={{
          title: "Go to content hub",
          icon: "paperflite-right-arrow",
          onClick: () => {
            navigate("/hub");
          },
        }}
      />
    );
  }

  const getShouldShowEditConversation = () => {
    if (isDealRoom()) {
      return userRoleService.canAccessDealRoom();
    }

    if (isCollection()) {
      return userRoleService.canAccessCollection();
    }

    return false;
  }

  const onEditConversationClick = () => {
    if (isDealRoom()) {
      redirectToCollection(`/deal-room/${_.get(conversation,"dealRoom.id","")}`);
    }

    else if (isCollection()) {
      if (isLiveCollection) {
        redirectToCollection(`/collections/${_.get(conversation, "collection.id")}`)
      } else {
        redirectToCollection(`/conversations/${id}/edit`)
      }
    }
  }

  const getEditConversationText = () => {
    if (isDealRoom()) {
      return "View deal room";
    }

    if (isCollection()) {
      if (isLiveCollection) {
        return "View live collection";
      } else {
        return "Edit collection";
      }
    }

    return "";
  }

  return (
    <>
      <section className={styles.banner}>
        <BannerView
          overlay={false}
          banner={_.get(conversation, "banner")}
          defaultBanner="/images/collections/background.jpeg"
        />
      </section>
      <section className={styles.conversationInfo}>
        <section className={styles.stickyContainer}>
          <Header
            conversation={conversation}
            loading={loading || loadingAssets}
            selectedFilter={selectedFilter}
            toggleTimeLine={setShowTimeLine}
            toggleMapDeals={setShowMapDeals}
            toggleDelete={setShowDelete}
            togglePreview={setShowPreview}
            toggleResend={setShowResend}
          />

          <Analytics
            conversation={conversation}
            handleDealValueBreakup={handleDealValueBreakup}
          />

          <animated.div style={childrenAnimation}>
            {(showDealValueBreakup && _.size(_.get(conversation,"opportunities",[])) > 0) && (
              <div ref={containerRef} className={styles.opportunitiesContainer}>
                <Row className={styles.opportunityItem}>
                  <Col xl={4} lg={4} className={styles.tableCell}>
                    <p className={styles.label}>Deal Name</p>
                  </Col>
                  <Col xl={2} lg={2} className={styles.tableCell}>
                    <p className={styles.label}>CRM</p>
                  </Col>
                  <Col xl={2} lg={2} className={styles.tableCell}>
                    <p className={styles.label}>Deal Value</p>
                  </Col>
                  <Col xl={3} lg={3}>
                    <p className={styles.label}>Deal Stage</p>
                  </Col>
                </Row>
                {_.get(conversation, "opportunities", []).map(
                  (opportunity: OpportunityType) => (
                    <Opportunity
                      opportunity={opportunity}
                      key={opportunity.id}
                      onRemove={() => setRemoveDeals({
                        show: true,
                        opportunity: opportunity.id
                      })}
                    />
                  ),
                )}
              </div>
            )}
          </animated.div>
        </section>
        <section className={styles.tabsSection}>
          <div className={styles.tabsContainer}>
            <TabBar
              gap={16}
              active={activeTab}
              onSelectionChange={(tab) => setActiveTab(tab)}
            >
              <div
                className={cx(styles.tab, { [styles.active]: activeTab == 0 })}
              >
                {`Recipients (${_.get(recipients, "pagination.totalRecordsCount") || 0})`}
              </div>

              {hasSections && (
                <div
                  className={cx(styles.tab, {
                    [styles.active]: activeTab == 1,
                  })}
                >
                  {`Sections (${_.size(sections)})`}
                </div>
              )}

              {!hasSections && _.size(assets) > 0 && (
                <div
                  className={cx(styles.tab, {
                    [styles.active]: activeTab == 2,
                  })}
                >
                  {`Assets (${_.size(assets)})`}
                </div>
              )}
            </TabBar>

            {activeTab === 0 && (
              <SortByAction
                sortOptions={sortOptions}
                value={sortPreferances}
                onChange={handleFilterChange}
              />
            )}

            {(activeTab === 1 || activeTab === 2) &&
              _.get(conversation, "canEdit") && (
                <span className={styles.collectionCta}>
                  {getShouldShowEditConversation() && (
                    <span
                      onClick={onEditConversationClick}
                      className={styles.editCollection}
                    >
                      <i
                        className={
                          isCollection() && !isLiveCollection
                            ? "paperflite-edit"
                            : "paperflite-eye"
                        }
                      />
                      {getEditConversationText()}
                    </span>
                  )}
                </span>
              )}
          </div>
          <TabSection index={0} selected={activeTab}>
            {<RecipientsContainer 
            conversation={conversationState} 
            loadMoreRecipientsForConversation={loadMoreRecipientsForConversation}
            />}
          </TabSection>
          <TabSection index={1} selected={activeTab}>
            {hasSections && (
              <div className={styles.sectionsContainer}>
                {_.map(sections, (section) => {
                  return (
                    <SectionShare
                      shareId={id}
                      fetchAnalytics={(assetId) => {
                        dispatch(
                          fetchConversationAssetAnalytics({
                            conversationId: id!,
                            assetId: assetId,
                            sectionId: section.id,
                            type: selectedFilter,
                          }),
                        );
                      }}
                      fetchAssetsForSection={() => {
                        dispatch(
                          fetchAssetsForSections({
                            conversation: id!,
                            section: section.id!,
                          }),
                        );
                      }}
                      key={section.id}
                      section={section}
                      source="conversation"
                    />
                  );
                })}
              </div>
            )}
          </TabSection>
          <TabSection index={2} selected={activeTab}>
            {_.size(assets) > 0 && !hasSections && (
              <div className={styles.assetsContainer}>
                {assets.map((asset) => {
                  return (
                    <AssetShare
                      asset={asset}
                      key={asset.id}
                      shareId={id}
                      source="conversation"
                      fetchAnalytics={(assetId) => {
                        dispatch(
                          fetchConversationAssetAnalytics({
                            conversationId: id!,
                            assetId: assetId,
                            type: selectedFilter,
                          }),
                        );
                      }}
                    />
                  );
                })}
              </div>
            )}
          </TabSection>
        </section>

        <MapDeals
          show={showMapDeals}
          onClose={() => setShowMapDeals(false)}
          conversation={conversation}
          onSelect={(opportunity: string) => {
            dispatch(
              addOpportunityToShare({
                conversationId: id!,
                opportunityId: opportunity,
              }),
            );
          }}
        />
        <TimeLine
          show={showTimeLine}
          onClose={() => setShowTimeLine(false)}
          conversation={conversation}
        />
        <Delete
          show={showDelete}
          onClose={() => setShowDelete(false)}
          conversation={conversation}
        />
        <Preview
          show={showPreview}
          onClose={() => setShowPreview(false)}
          conversation={conversation}
        />
        <Resend
          show={showResend}
          onClose={() => setShowResend(false)}
          conversation={conversation}
        />
        <RemoveDeal show={removeDeal.show} opportunity = {removeDeal.opportunity}  onClose={() => setRemoveDeals({
          show: false,
          opportunity: null
        })}
        conversationId={id!}
        onRemove={() => {
          dispatch(
            removeOpportunityFromConversation({
              conversationId: id!,
              opportunityId: removeDeal.opportunity!,
            }),
          );
        }}
        />
      </section>
    </>
  );
};

export default ConversationDetail;

//components
interface IHeader {
  conversation: Conversation;
  loading: boolean;
  selectedFilter: string;
  toggleMapDeals: (value: boolean) => void;
  toggleTimeLine: (value: boolean) => void;
  toggleDelete: (value: boolean) => void;
  togglePreview: (value: boolean) => void;
  toggleResend: (value: boolean) => void;
}

const Header: React.FC<IHeader> = ({
  conversation,
  loading,
  selectedFilter,
  toggleMapDeals,
  toggleTimeLine,
  toggleDelete,
  togglePreview,
  toggleResend,
}) => {
  const { trackPreviewClicked } = useConversationAdoption();
  const seekEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.seekEnabled", false),
  );
  const paperfliteEmailDisabled = useSelector((state: any) => {
    return (
      !IntegraionService.getIntegrationForCategory("email") &&
      !_.get(state, "account.paperfliteEmailServerEnabled", true)
    );
  });

  const getConversationTitle = () => {
    if (_.get(conversation, "collection")) {
      return _.get(conversation, "collection.name", "");
    } else if (_.get(conversation, "subject")) {
      return _.get(conversation, "subject", "");
    } else {
      return "No Subject";
    }
  };


  const getCampaignTooltip = () => {
    const utmCampaign = _.get(conversation, "utmCampaign", {
      name: "",
      source: "",
      medium: "",
      term: "",
      content: "",
    });
    const tooltipContent = Object.entries({
      Name: utmCampaign.name,
      Source: utmCampaign.source,
      Medium: utmCampaign.medium,
      Term: utmCampaign.term,
      Content: utmCampaign.content,
    })
      .filter(([, value]) => value !== "" && value !== null)
      .map(([key, value]) => `${key} : ${value}`)
      .join(", ");

    return tooltipContent;
  };
  const canReshareConversation = () => {
    if (
      !loading &&
      _.get(conversation, "body") &&
      selectedFilter !== "anonymous" &&
      _.get(conversation, "canEdit") &&
      userRoleService.canShare() &&
      !paperfliteEmailDisabled
    ) {
      return true;
    } else {
      return false;
    }
  };
  const renderActionBar = () => {
    return (
      <>
        {_.get(conversation, "enabledIntegration", false) && (
          <Actions.IconButton
            icon="paperflite-map-deal"
            tooltip="Map deal"
            onClick={() => toggleMapDeals(true)}
          />
        )}

        {!loading &&
          _.get(conversation, "body") &&
          selectedFilter !== "anonymous" && (
            <Actions.IconButton
              icon="paperflite-eye"
              tooltip="Preview"
              onClick={() => {
                togglePreview(true);
                trackPreviewClicked();
              }}
            />
          )}

        {canReshareConversation() && (
          <Actions.IconButton
            icon="paperflite-resend"
            tooltip="Resend"
            onClick={() => toggleResend(true)}
          />
        )}

        <Actions.IconButton
          icon="paperflite-timeline"
          tooltip="Timeline"
          onClick={() => toggleTimeLine(true)}
        />

        {_.get(conversation, "canEdit") && !loading && (
          <Actions.IconButton
            icon="paperflite-delete"
            tooltip="Delete"
            onClick={() => toggleDelete(true)}
          />
        )}

        {seekEnabled && <Actions.Seek />}
      </>
    );
  };
  const getConversationSource = () => {
    if (!_.get(conversation, "source")) {
      return "";
    } else {
      const source = _.get(conversation, "source", "") as string;
      switch (source) {
        case "Paperflite QuickSend":
          return "QuickSend";
        case "Paperflite Link":
          return "Link";
        default:
          return source;
      }
    }
  };
  return (
    <React.Fragment>
      <div className={styles.headerContainer}>
        <span className={styles.title}>
          <Text lines={2} tooltip={{placement:"auto"}} text={getConversationTitle()} />
          {_.get(conversation, "utmCampaign.name") && (
            <span className={styles.campaignInfo}>
              <Tooltip placement="right" content={getCampaignTooltip()}>
                <i className={cx("paperflite-info", styles.icon)}></i>
              </Tooltip>
            </span>
          )}
        </span>
        <div className={styles.actionbar}>{renderActionBar()}</div>
      </div>
      <div className={styles.shareInfo}>
        <p className={styles.title}>
          You have shared {_.get(conversation, "totalAssetsCount")}{" "}
          {pluralize("asset", _.get(conversation, "totalAssetsCount"))} with{" "}
          {_.get(conversation, "totalRecipients")}{" "}
          {pluralize("recipient", _.get(conversation, "totalRecipients"))}
        </p>
        <p className={styles.subTitle}>
          <TimeAgo date={_.get(conversation, "createdDate")} live={false} />

          {_.get(conversation, "source") && 
            <span> via {getConversationSource()}</span>
          }
        </p>
      </div>
    </React.Fragment>
  );
};

interface IAnalytics {
  conversation: Conversation;
  handleDealValueBreakup: () => void;
}
const Analytics: React.FC<IAnalytics> = ({
  conversation,
  handleDealValueBreakup,
}) => {
  const calculateDealValue = (opportunities) => {
    const totalDealValue = opportunities.reduce(
      (totalDealValue, opportunity) => totalDealValue + opportunity.amount,
      0,
    );
    return totalDealValue;
  };

  const currency = useSelector((state) =>
  _.get(state, "accounts.item.currency", "")
);

  const [toggleAction, setToggleAction] = React.useState(false);
  return (
    <div className={styles.infoCardsContainer}>
      <Metrics
        metricValue={generateMetricPercentage(
          _.get(conversation, "uniqueEmailOpens", 0) /
            _.get(conversation, "totalRecipients", 0),
        )}
        icon="paperflite-mail-open"
        metricName="email open rate"
        disabled = { _.get(conversation, "uniqueEmailOpens", 0) === 0}
      />
      <Metrics
        metricValue={generateMetricPercentage(
          _.get(conversation, "uniqueLinkClicks", 0) /
            _.get(conversation, "totalRecipients", 0),
        )}
        icon="paperflite-click-rate"
        metricName="link click rate"
        disabled = { _.get(conversation, "uniqueLinkClicks", 0) === 0}
      />
      <Metrics
        metricValue={
          generateTimeSpent(_.get(conversation, "totalTimeSpent", 0)) ||
          "00 : 00"
        }
        icon="paperflite-clock"
        metricName="total time spent"
        disabled = { _.get(conversation, "totalTimeSpent", 0) === 0}
      />
      {_.size(_.get(conversation, "opportunities")) > 0 && (
        <Metrics
          metricValue={calculateDealValue(conversation.opportunities)}
          icon={_.isEmpty(currency) ? "" : "paperflite-dollar"}
          metricName="deal value"
          action={
            <i
              className={cx("paperflite-dropdown", {
                [styles.toggleAction]: toggleAction,
              })}
              onClick={() => {
                setToggleAction(!toggleAction);
                handleDealValueBreakup();
              }}
            />
          }
        />
      )}
    </div>
  );
};
interface IMetrics {
  metricValue: string;
  metricName: string;
  icon: string;
  action?: React.ReactNode;
  disabled?: boolean;
}

const Metrics: React.FC<IMetrics> = ({
  metricValue,
  metricName,
  icon,
  action,
  disabled = false
}) => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.metrics}>
        <span className={cx(styles.metricValue, {
          [styles.disabled]: disabled
        })}>{metricValue}</span>
        <i className={cx(icon, styles.icon)} />
      </div>

      <div className={styles.actions}>
        <span className={styles.metricName}>{metricName}</span>
        {action && action}
      </div>
    </div>
  );
};

interface IOpportunity {
  opportunity: OpportunityType;
  onRemove: () => void;
}

const Opportunity: React.FC<IOpportunity> = ({
  opportunity,
  onRemove,
}) => {

  const currency = useSelector((state: any) =>
    _.get(state, "accounts.item.currency", "")
  );
  const renderCRMName = (integrationType: string | null) => {
    const integration = IntegraionService.getIntegrations().integrations.find(
      (data) => {
        return data.type === integrationType;
      },
    );

    if (integration) {
      return integration.name;
    }
    return integrationType;
  };

  return (
    <Row className={styles.opportunityItem}>
      <Col xl={4} lg={4} className={styles.tableCell}>
        <p className={styles.value}>{opportunity.name}</p>
      </Col>
      <Col xl={2} lg={2} className={styles.tableCell}>
        <p className={styles.value}>
          {renderCRMName(opportunity.integrationType)}
        </p>
      </Col>
      <Col xl={2} lg={2} className={styles.tableCell}>
        <p className={styles.value}>
          {_.get(opportunity, "amount", 0) >= 0
           ? `${currency}${_.get(opportunity, "amount", 0)}`
           : `-${currency}${Math.abs(_.get(opportunity, "amount", 0))}`}        </p>
      </Col>
      <Col xl={3} lg={3}>
        <p className={styles.value}>{opportunity.stageValue}</p>
      </Col>
      <Col xl={1} lg={1} className={styles.action}>
        <span
          onClick={onRemove}
        >
          <i className="paperflite-delete" />
          <span>{opportunity.removing ? "Removing" : "Remove"}</span>
        </span>
      </Col>
    </Row>
  );
};

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      opacity: active ? 1 : 0,
      marginTop: active ? '0' : '5rem',
    },
    [selected],
  );

  return active ? (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  ) : (
    <></>
  );
};
